
import type { MarriageAskRow, MarriageStatus } from "../net/messages";
import { elem, styleInstaller } from "./chrome";

export interface MarriageActions {
  /** Accept or decline, naming the token of the row the button belongs to. */
  onAnswer?: (token: number, accept: boolean) => void;
}

/** `MarriageStatus.state`. Wire values, and the server's alone. */
export const STATE_NONE = 0;
export const STATE_ENGAGED = 1;
export const STATE_MARRIED = 2;

/** `MarriageAskRow.kind`, the four `confirm()` dialogs of
 *  `marriage_manage.quest:173,252,322,400`. */
export const ASK_PROPOSAL = 0;
export const ASK_CEREMONY = 1;
export const ASK_DIVORCE = 2;
export const ASK_END_WEDDING = 3;

export function describeAsk(ask: MarriageAskRow): string {
  const who = ask.from_name || "Somebody";
  if (ask.kind === ASK_CEREMONY || ask.kind === ASK_PROPOSAL) {
    return `${who} has proposed to you. Would you like to accept?`;
  }
  if (ask.kind === ASK_DIVORCE) {
    return `${who}: Do you agree to the divorce?`;
  }
  return "Do you want to end this wedding?";
}

const STYLE_ID = "marriage-style";

const STYLE = `
.marriage-ask {
  position: fixed;
  left: 50%;
  top: 42%;
  transform: translateX(-50%);
  z-index: 63;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: auto;
}
.marriage-ask[hidden] { display: none; }
.marriage-ask-card {
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.95);
  border: 1px solid rgba(255, 160, 190, 0.34);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  font: 13px/1.5 system-ui, sans-serif;
  color: #d8dee6;
  text-align: center;
}
.marriage-ask-text { margin-bottom: 2px; }
.marriage-ask-clock { color: #8fa0b4; font-size: 11px; margin-bottom: 8px; }
.marriage-ask-buttons { display: flex; gap: 8px; }
.marriage-ask-button {
  flex: 1;
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid rgba(150, 200, 255, 0.34);
  background: rgba(150, 200, 255, 0.1);
  color: #cfe2ff;
  font: inherit;
  cursor: pointer;
}
.marriage-ask-button:hover:not(:disabled) { background: rgba(150, 200, 255, 0.2); }
.marriage-ask-button:disabled { opacity: 0.4; cursor: default; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class MarriageAsk {
  private readonly asks: HTMLElement;

  /** Tokens this dialog has already answered, so a second click on one prompt
   *  cannot become a second answer. A set rather than a single value, because
   *  two prompts can be on screen at once and answering one must not disarm the
   *  other. */
  private readonly answered = new Set<number>();

  /** The last frame drawn. Kept only so the tests and `shownTokens` can read
   *  what is on screen; nothing about the marriage is computed from it. */
  private drawn: MarriageStatus | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: MarriageActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.asks = elem(doc, "div", "marriage-ask");
    this.asks.hidden = true;
    container.append(this.asks);
  }

  get asking(): boolean {
    return !this.asks.hidden;
  }

  /** The tokens of the prompts on screen, in the order they are drawn. For
   *  tests, and for the same reason `ArenaPanel.shownToken` exists: the whole
   *  claim of the token is that the dialog's and the server's can differ. */
  get shownTokens(): number[] {
    return this.drawn === null ? [] : this.drawn.asks.map((ask) => ask.token);
  }

  /** Draw the questions of one `MarriageStatus`. The rest of the frame is the
   *  messenger's row (`FriendsPanel.setLover`). */
  update(status: MarriageStatus): void {
    this.drawn = status;
    this.drawAsks(status.asks);
  }

  private drawAsks(asks: readonly MarriageAskRow[]): void {
    const doc = this.asks.ownerDocument;
    this.asks.replaceChildren();
    const live = asks.filter((ask) => !this.answered.has(ask.token));
    for (const ask of live) {
      const card = elem(doc, "div", "marriage-ask-card");
      const text = elem(doc, "div", "marriage-ask-text", describeAsk(ask));
      const clock = elem(doc, "div", "marriage-ask-clock");
      clock.textContent = `${ask.seconds}s to answer`;
      const buttons = elem(doc, "div", "marriage-ask-buttons");
      const yes = doc.createElement("button");
      yes.type = "button";
      yes.className = "marriage-ask-button";
      yes.textContent = "Yes";
      yes.addEventListener("click", () => this.answer(ask.token, true));
      const no = doc.createElement("button");
      no.type = "button";
      no.className = "marriage-ask-button";
      no.textContent = "No";
      no.addEventListener("click", () => this.answer(ask.token, false));
      buttons.append(yes, no);
      card.append(text, clock, buttons);
      this.asks.append(card);
    }
    this.asks.hidden = live.length === 0;
  }

  private answer(token: number, accept: boolean): void {
    // The token of the row this button was built from. Never "the first ask in
    // the frame" and never the connection's current one: two questions can be
    // on screen at once and they are not interchangeable -- one of them is a
    // proposal and the other may be a divorce.
    if (this.answered.has(token)) return;
    this.answered.add(token);
    this.actions.onAnswer?.(token, accept);
    // Taken down at once, because a prompt that stayed up until the server
    // answered would be a prompt a second click could reach. The server's next
    // frame is what says what actually happened.
    if (this.drawn !== null) this.drawAsks(this.drawn.asks);
  }
}
